// アプリシェル: ブランドヘッダ＋ナビ＋アクティブビュー＋行選択/新規/削除（Slice 2 完了分）。
import { useEffect, useState } from "preact/hooks";
import { listApps } from "./api/client";
import {
  appSig,
  activeViewSig,
  activeViewIdSig,
  activeRowsSig,
  statusSig,
  errorSig,
  syncSig,
  isOnlineSig,
  pendingSig,
  selectedRowSig,
  viewStackSig,
  loadApp,
  setActiveView,
  navigateTo,
  openRow,
  openRowIn,
  goBack,
  createRow,
  deleteRow,
  formViewFor,
  editCell,
} from "./store/store";
import { ViewRenderer } from "./render/View";
import { resolveTokens, label, cssVars } from "./render/model.mjs";
import { currentMode, toggleTheme } from "./render/theme";
import { LoginView } from "./auth/LoginView";
import { BuilderView } from "./builder/BuilderView";
import { SheetsTokenPanel } from "./auth/SheetsTokenPanel";
import { requiresLoginSig, sessionSig, logout } from "./auth/session";
import { IconMoon, IconSun, IconPlus, IconArrowLeft, IconTrash, IconWifiOff, IconAlert } from "./render/icons";
import type { Row } from "./types";

/** 読み込みエラーが「Sheets トークン起因」か（未送付=400 / 無効・期限切れ=502 の文言で判定）。 */
function isSheetsTokenError(msg: string | null): boolean {
  return !!msg && /トークンが必要|認証エラー/.test(msg);
}

const LAST_APP_KEY = "sheetsnap:lastApp";

/** URL の ?app= が正。無ければ最後に開いたアプリへ（A2HS の standalone 起動はクエリを失うため / D-0042）。 */
function params() {
  const q = new URLSearchParams(location.search);
  let appId = q.get("app") ?? "";
  if (!appId) {
    try { appId = localStorage.getItem(LAST_APP_KEY) ?? ""; } catch { /* 取得不可なら案内画面 */ }
  }
  return { appId, draft: q.get("draft") === "1", builder: q.get("builder") === "1" };
}

function rememberApp(appId: string): void {
  try { localStorage.setItem(LAST_APP_KEY, appId); } catch { /* 保存不可でも動作に影響なし */ }
}

/** 保存状態の小さなピル（保存中/保存済み/競合/失敗/オフライン保留）。 */
function SyncPill() {
  const s = syncSig.value.status;
  const pending = pendingSig.value;
  if (s === "idle") return null;
  const map: Record<string, { t: string; c: string }> = {
    saving: { t: "保存中…", c: "ss-pill-saving" },
    saved: { t: "保存済み", c: "ss-pill-saved" },
    conflict: { t: "競合", c: "ss-pill-warn" },
    error: { t: "保存失敗", c: "ss-pill-err" },
    offline: { t: pending > 0 ? `未送信 ${pending} 件` : "オフライン", c: "ss-pill-warn" },
  };
  const m = map[s];
  if (!m) return null;
  return <span class={"ss-pill " + m.c}>{m.t}</span>;
}

export function App() {
  const requiresLogin = requiresLoginSig.value;
  // ③ 下部シートバー: 自分のアプリ（=シート）一覧。切替は ?app= を書き換えて再読込（状態を単純に保つ）。
  const [myApps, setMyApps] = useState<Array<{ id: string; name?: string }>>([]);

  // 未ログイン（本番）ではアプリを読み込まない。ログイン成立でこの effect が再実行され読み込む。
  useEffect(() => {
    const { appId, draft, builder } = params();
    if (appId && !requiresLogin && !builder) {
      rememberApp(appId); // A2HS の standalone 起動（クエリ無し）で復元するため
      void loadApp(appId, { draft });
    }
    // シートバー用の一覧（失敗は無視＝バー非表示なだけ）
    if (!requiresLogin && !builder) listApps().then(setMyApps).catch(() => {});
  }, [requiresLogin]);

  // PF34: 本番で未ログインならログイン画面（ローカルは devUser があるので素通り）
  if (requiresLogin) return <LoginView />;

  // ② Theme Studio（ビルダー）: ?builder=1 でランタイムの代わりに編集画面（ログイン後）
  if (params().builder) return <BuilderView />;

  const status = statusSig.value;
  const app = appSig.value;
  const { appId } = params();

  if (!appId) {
    return (
      <div class="ss-center">
        <div class="ss-hint">
          <strong>SheetSnap Runtime</strong>
          <p>URL に <code>?app=&lt;アプリID&gt;</code> を付けて開いてください（下書きは <code>&amp;draft=1</code>）。</p>
        </div>
      </div>
    );
  }
  if (status === "loading" || status === "idle") return <div class="ss-center"><div class="ss-spinner" /></div>;
  if (status === "error") {
    // Sheets 接続トークン起因（未送付/無効）→ 貼り付けパネル（D-0043・Slice 5c までの暫定）
    if (isSheetsTokenError(errorSig.value)) {
      const isInvalid = /認証エラー/.test(errorSig.value ?? "");
      return (
        <SheetsTokenPanel
          error={isInvalid ? "トークンが無効か期限切れです。新しいトークンを貼り付けてください。" : null}
          onConnect={() => { const p = params(); void loadApp(p.appId, { draft: p.draft }); }}
        />
      );
    }
    return <div class="ss-center"><div class="ss-error">読み込みエラー: {errorSig.value}</div></div>;
  }
  if (!app) return null;

  const mode = currentMode(app.brand?.base);
  // html に data-theme を反映（シェル外＝body/ログイン等の CSS フォールバックにも効かせる）。
  if (typeof document !== "undefined") document.documentElement.setAttribute("data-theme", mode);
  const tokens = resolveTokens(app, mode);
  const loc = app.settings?.defaultLocale ?? "ja";
  const view = activeViewSig.value;
  const rows: Row[] = activeRowsSig.value;
  const selectedRow = selectedRowSig.value;
  const canGoBack = viewStackSig.value.length > 0;
  const evalCtx = { now: new Date() }; // 式評価器(expression.mjs)は Date を期待

  // ＋新規は「一覧系ビュー ＆ 同テーブルの form ビューがある」ときだけ出す
  const isCollection = !!view && ["list", "table", "gallery", "board", "map", "calendar"].includes(view.type);
  const canCreate = isCollection && !!formViewFor(app, view!.tableId);
  // 削除は detail/form で対象行が確定しているときだけ出す
  const isRowPage = !!view && (view.type === "detail" || view.type === "form");
  const deletableRow = isRowPage ? selectedRow ?? rows[0] ?? null : null;

  const onDelete = () => {
    if (!deletableRow?.__rowId) return;
    if (!window.confirm("この行を削除しますか？")) return;
    void deleteRow(deletableRow);
    if (canGoBack) goBack();
  };

  const onAction = (actionId: string, row?: Row) => {
    const action = app.actions?.find((a) => a.id === actionId);
    if (action?.kind === "navigate") {
      const viewId = (action.params as { viewId?: string } | undefined)?.viewId;
      if (!viewId) return;
      // 行コンテキスト付き（カード上のボタン）なら行を選択してから遷移
      if (row?.__rowId) openRowIn(row, viewId);
      else void setActiveView(viewId);
    } else if (action?.kind === "open-url") {
      const url = (action.params as { url?: string } | undefined)?.url;
      if (url) window.open(url, "_blank", "noopener");
    } else if (action?.kind === "create-row") {
      createRow();
    } else if (action?.kind === "delete-row") {
      const target = row ?? deletableRow;
      if (target?.__rowId && window.confirm("この行を削除しますか？")) {
        void deleteRow(target);
        if (canGoBack) goBack();
      }
    }
    // update-row(setValues)/share-export は次スライス
  };

  return (
    <div class={"ss-app" + (myApps.length > 1 ? " has-sheetbar" : "")} style={cssVars(tokens)}>
      <header class="ss-header">
        {canGoBack ? (
          <button class="ss-back" onClick={() => goBack()} aria-label="戻る"><IconArrowLeft /></button>
        ) : (
          <span class="ss-logo" style={`background:${tokens["color.brand.primary"]}`} />
        )}
        <span class="ss-title">{label(app.name, loc)}</span>
        <SyncPill />
        <button
          class="ss-theme-toggle"
          title={mode === "dark" ? "ライトモードに切り替え" : "ダークモードに切り替え"}
          aria-label="テーマ切り替え"
          onClick={() => toggleTheme(mode)}
        >
          {mode === "dark" ? <IconSun /> : <IconMoon />}
        </button>
        {sessionSig.value?.user && (
          <button
            class="ss-account"
            title={`${sessionSig.value.user.email}${sessionSig.value.user.plan ? `（${sessionSig.value.user.plan}）` : ""} — クリックでログアウト`}
            onClick={() => { if (window.confirm("ログアウトしますか？")) logout(); }}
          >
            {sessionSig.value.user.email.slice(0, 1).toUpperCase()}
          </button>
        )}
      </header>

      {syncSig.value.status === "conflict" && (
        <div class="ss-banner ss-banner-warn">
          <IconAlert size={14} /> 他の編集と競合したため最新の内容に更新しました（{(syncSig.value.conflicts ?? []).join(", ")}）。もう一度入力してください。
        </div>
      )}
      {syncSig.value.status === "error" && (
        <div class="ss-banner ss-banner-err">保存に失敗しました: {syncSig.value.error}</div>
      )}
      {!isOnlineSig.value && (
        <div class="ss-banner ss-banner-warn">
          <IconWifiOff size={14} /> オフラインです。編集は端末に保存され、接続が戻ると自動で送信されます
          {pendingSig.value > 0 ? `（未送信 ${pendingSig.value} 件）` : ""}。
        </div>
      )}

      {app.navigation?.length > 0 && (
        <nav class="ss-nav">
          {app.navigation.map((n) => (
            <button
              key={n.id}
              class={"ss-nav-btn" + (activeViewIdSig.value === n.viewId ? " is-active" : "")}
              onClick={() => navigateTo(n.viewId)}
            >
              {label(n.label, loc)}
            </button>
          ))}
        </nav>
      )}

      <main class="ss-main">
        {view ? (
          <>
            <ViewRenderer
              app={app}
              view={view}
              rows={rows}
              locale={loc}
              mode={mode}
              evalCtx={evalCtx}
              onAction={onAction}
              onInput={editCell}
              onSelectRow={isCollection ? openRow : undefined}
              selectedRow={selectedRow}
            />
            {isRowPage && deletableRow?.__rowId && (
              <button class="ss-danger" onClick={onDelete}><IconTrash size={15} /> この行を削除</button>
            )}
          </>
        ) : (
          <div class="ss-hint">表示できるビューがありません。</div>
        )}
      </main>

      {canCreate && (
        <button class="ss-fab" onClick={() => createRow()} aria-label="新規作成"><IconPlus size={24} /></button>
      )}

      {/* ③ 下部シートバー（AppSheet 風ボトムナビ）: シートの数だけアイコンタブ・タップで切替 */}
      {myApps.length > 1 && (
        <nav class="ss-sheetbar" aria-label="シート切替">
          {myApps.map((a) => {
            const name = a.name ?? a.id;
            const active = a.id === appId;
            return (
              <button
                key={a.id}
                class={"ss-sheettab" + (active ? " is-active" : "")}
                title={name}
                aria-current={active ? "page" : undefined}
                onClick={() => { if (!active) { rememberApp(a.id); location.href = `/?app=${encodeURIComponent(a.id)}`; } }}
              >
                <span class="ss-sheettab-i">{name.slice(0, 1)}</span>
                <span class="ss-sheettab-t">{name}</span>
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}
